.dialog {
  width: min(520px, calc(100% - 32px));
  /* iPhone (app en beginscherm, viewport-fit=cover): niet onder de statusbalk of home-indicator. */
  max-height: calc(100% - 64px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  padding: 0;
  border: 0;
  border-radius: var(--radius-xl);
  background: var(--surface-panel);
  color: var(--text);
  box-shadow: var(--shadow-3);
  overflow: auto;
}

.dialog::backdrop {
  background: var(--scrim);
}

.dialog[open] {
  animation: dialog-in var(--dur-slow) var(--ease-out);
}

.dialog[open]::backdrop {
  animation: fade-in var(--dur-slow) var(--ease-out);
}

.panel {
  padding: var(--space-5);
}

.head {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
}

.headText {
  flex: 1 1 auto;
  min-width: 0;
}

.title {
  margin: 0;
  font-size: var(--text-xl);
  font-weight: 600;
  line-height: var(--line-tight);
}

.description {
  margin: 6px 0 0;
  color: var(--text-muted);
  font-size: var(--text-md);
}

.close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--touch-min);
  height: var(--touch-min);
  margin: -8px -8px 0 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.close:hover {
  background: var(--surface-hover);
  color: var(--text);
}

.close:focus-visible {
  outline: 3px solid var(--focus-ring);
  outline-offset: 2px;
}

.body {
  max-width: 100%;
  margin-bottom: var(--space-4);
  /* Niets in een dialoog of sheet mag de pagina opzij laten schuiven (390 px). */
  overflow-x: hidden;
  overflow-wrap: break-word;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-2);
}

/* Sheet: op mobiel van onderen, op brede schermen rechts; zelfde inhoud. */
.sheet {
  margin: auto 0 0;
  width: 100%;
  max-width: none;
  max-height: 88%;
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
}

.sheet[open] {
  animation: sheet-up var(--dur-slow) var(--ease-out);
}

/* Lange formulieren in een sheet: de knoppen blijven onderaan in beeld (één primaire actie). */
.sheet > .panel > .actions {
  position: sticky;
  bottom: 0;
  z-index: 1;
  margin: 0 calc(-1 * var(--space-5)) calc(-1 * var(--space-5));
  padding: var(--space-3) var(--space-5) max(var(--space-3), env(safe-area-inset-bottom));
  background: var(--surface-panel);
  box-shadow: 0 -1px 0 var(--border-subtle);
}

.sheet > .panel > .body {
  margin-bottom: var(--space-3);
  /* Zolang er een melding boven de knoppenbalk staat, blijft er evenveel ruimte onder de inhoud:
     de laatste rit en zijn knoppen schuiven erboven in beeld (Toast.tsx meet de meldingen). */
  padding-bottom: var(--dialog-toast-h, 0px);
}

@media (min-width: 720px) {
  .sheet {
    /* Logisch (golf 3): aan de eindkant van het scherm, in het Arabisch dus links. */
    margin: 0;
    margin-inline-start: auto;
    width: 480px;
    height: 100%;
    max-height: none;
    border-radius: 0;
    border-start-start-radius: var(--radius-xl);
    border-end-start-radius: var(--radius-xl);
  }

  .sheet[open] {
    animation: sheet-left var(--dur-slow) var(--ease-out);
  }

  [dir='rtl'] .sheet[open] {
    animation-name: sheet-right;
  }
}

@media (max-width: 480px) {
  .panel {
    padding: var(--space-4);
  }

  .actions {
    flex-direction: column-reverse;
  }

  .sheet > .panel > .actions {
    flex-direction: row;
    margin: 0 calc(-1 * var(--space-4)) calc(-1 * var(--space-4));
    padding-left: var(--space-4);
    padding-right: var(--space-4);
  }

  .sheet > .panel > .actions > * {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
  }

  .actions > * {
    width: 100%;
  }
}

@keyframes dialog-in {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
}

@keyframes sheet-up {
  from {
    transform: translateY(24px);
    opacity: 0;
  }
}

@keyframes sheet-left {
  from {
    transform: translateX(24px);
    opacity: 0;
  }
}

@keyframes sheet-right {
  from {
    transform: translateX(-24px);
    opacity: 0;
  }
}

/* Golf 2 (oprichter #11): de sheet uitgeklapt tot volledig scherm (afspraak-editor). Zelfde
   element en inhoud, alleen meer ruimte; op brede schermen staat de inhoud rustig in het midden. */
.expand {
  margin-inline-end: 0;
}

.dialog.full,
.sheet.full {
  margin: 0;
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  border-radius: 0;
}

.full > .panel {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  max-width: 1120px;
  margin: 0 auto;
}

.full > .panel > .body {
  flex: 1 1 auto;
}

/* Kept outside the scrolling form, inside the modal top layer. */
.edgeExpand {
  position: fixed;
  top: 50%;
  left: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--b-space-40);
  height: var(--b-control-large);
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: 0 var(--radius-md) var(--radius-md) 0;
  background: var(--surface-panel);
  color: var(--text);
  box-shadow: var(--shadow-2);
  cursor: pointer;
}
.edgeExpand:hover {
  background: var(--surface-hover);
}
.edgeExpand:focus-visible {
  outline: 3px solid var(--focus-ring);
  outline-offset: -3px;
}
.withEdge > .panel {
  /* stylelint-disable-next-line declaration-property-value-disallowed-list -- ruimte voor de randknop van het blad */
  padding-inline-start: 52px;
}
/* Arabisch: de sheet staat links, dus de randknop aan de rechterrand ervan en de pijl gespiegeld. */
[dir='rtl'] .edgeExpand {
  left: auto;
  right: 0;
  border-radius: var(--radius-md) 0 0 var(--radius-md);
}
[dir='rtl'] .edgeExpand svg {
  transform: scaleX(-1);
}
@media (min-width: 720px) {
  .sheet .edgeExpand {
    left: calc(100% - 480px);
  }
  .sheet.full .edgeExpand {
    left: 0;
  }
  [dir='rtl'] .sheet .edgeExpand {
    left: auto;
    right: calc(100% - 480px);
  }
  [dir='rtl'] .sheet.full .edgeExpand {
    right: 0;
  }
}
/* Telefoon (oprichter 1 okt): de sheet is daar al schermbreed, dus geen randknop. Sheets met de
   standaardknop tonen dan ook de kopknop niet; de afspraak-editor houdt zijn eigen kopknop. */
@media (max-width: 719.98px) {
  .sheet .edgeExpand,
  .autoExpand .expand {
    display: none;
  }
  .sheet.withEdge > .panel {
    padding-inline-start: var(--space-5);
  }
  .autoExpand.full {
    margin: auto 0 0;
    height: auto;
    max-height: 88%;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  }
}
@media (max-width: 480px) {
  .sheet.withEdge > .panel {
    padding-inline-start: var(--space-4);
  }
}
/* Telefoon compact (oprichter 1 okt): een sheet met een formulier vult het hele scherm, met de
   kop bovenaan en de knoppen onderaan vast in beeld. Korte sheets (menu Meer, een afspraak
   bekijken) blijven een sheet van onderen. Ruimte voor de inkeping en de veilige zone. */
@media (max-width: 639.98px) {
  .panel {
    padding: var(--space-4);
  }

  .title {
    font-size: var(--text-lg);
  }

  .sheet:has(form) {
    margin: 0;
    height: 100%;
    max-height: none;
    border-radius: 0;
  }

  .sheet:has(form) > .panel {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    padding-top: 0;
  }

  .sheet:has(form) > .panel > .body {
    flex: 1 1 auto;
  }

  .sheet:has(form) > .panel > .head {
    position: sticky;
    top: 0;
    z-index: 2;
    align-items: center;
    margin: 0 calc(-1 * var(--space-4)) var(--space-3);
    padding: max(var(--space-2), env(safe-area-inset-top)) var(--space-2) var(--space-2)
      var(--space-4);
    background: var(--surface-panel);
    box-shadow: 0 1px 0 var(--border-subtle);
  }

  .sheet:has(form) .close {
    margin: 0;
  }

  /* Volledig scherm is hier al de vorm: de knop ervoor heeft geen zin. */
  .sheet:has(form) .expand {
    display: none;
  }
}

/* iPhone met inkeping en home-indicator (iPhone-app en beginschermapp, viewport-fit=cover): een
   volledig scherm begint onder de statusbalk, en een sheet zonder knoppenbalk eindigt boven de
   home-indicator (met knoppenbalk zorgt .actions daar zelf voor). Formulier-sheets op de telefoon
   houden hun eigen kop met inkepingsruimte (.sheet:has(form) > .panel wint op specificiteit). */
.full > .panel {
  padding-top: max(var(--space-5), env(safe-area-inset-top));
}

.sheet > .panel:not(:has(> .actions)) {
  padding-bottom: max(var(--space-5), env(safe-area-inset-bottom));
}

@media (max-width: 639.98px) {
  .full > .panel {
    padding-top: max(var(--space-4), env(safe-area-inset-top));
  }

  .sheet > .panel:not(:has(> .actions)) {
    padding-bottom: max(var(--space-4), env(safe-area-inset-bottom));
  }
}

/* UX-ronde 4 okt: in een sheet blijft de kop (titel en sluitknop) bovenaan staan bij scrollen, met
   een haarlijn eronder; net als de knoppenbalk onderaan. */
.sheet > .panel > .head {
  position: sticky;
  top: 0;
  z-index: 2;
  margin: calc(-1 * var(--space-5)) calc(-1 * var(--space-5)) var(--space-3);
  padding: var(--space-4) var(--space-5) var(--space-3);
  background: var(--surface-panel);
  box-shadow: 0 1px 0 var(--border-subtle);
}

.sheet.withEdge > .panel > .head {
  margin-inline-start: -52px;
  /* stylelint-disable-next-line declaration-property-value-disallowed-list -- ruimte voor de randknop van het blad */
  padding-inline-start: 52px;
}

@media (max-width: 719.98px) {
  .sheet.withEdge > .panel > .head {
    margin-inline-start: calc(-1 * var(--space-5));
    padding-inline-start: var(--space-5);
  }
}

@media (max-width: 480px) {
  .sheet > .panel > .head,
  .sheet.withEdge > .panel > .head {
    margin: calc(-1 * var(--space-4)) calc(-1 * var(--space-4)) var(--space-3);
    padding: var(--space-3) var(--space-4);
  }
}
